/**
 * Ephemeral Test-Fit Studio workstation state (zustand, module-scoped).
 *
 * NOTHING here is persisted or replayed: selection, active tool, projection,
 * workflow mode and pane visibility are view state only. Everything that
 * affects geometry flows through `patchLayout` into the frozen scenario input
 * instead. The store resets whenever the hosting scenario changes
 * (`resetForScenario`), so a stale selection can never point into another
 * scenario's plan.
 */
import { create } from 'zustand';

/** Drawing/interaction tool. 'select' is the default pointer. */
export type StudioTool =
  | 'select'
  | 'frontline'
  | 'barrier-wall'
  | 'barrier-berm'
  | 'barrier-building'
  // Place tools: one click on the map drops a default-size rect custom of
  // that kind (immediately selected for numeric editing in the inspector).
  | 'place-parking'
  | 'place-building'
  | 'place-data-center'
  | 'place-generator'
  // Sketch tools for custom geometry: road centerline (width prompted after
  // drawing) and free polygon.
  | 'draw-road'
  | 'draw-custom-polygon'
  // Assist tool: one click sets the entrance, then planning-grade access roads
  // are drawn from it to every placed equipment component.
  | 'connect-roads';

/** Canvas projection: world basemap or the local-frame blueprint sheet. */
export type StudioProjection = 'map' | 'blueprint';

/** The mutually-exclusive tab shown in the left workstation dock. */
export type StudioLeftPane = 'layout' | 'layers';

/**
 * @deprecated Retained while TestFitStudio hosts migrate from the old two-row
 * ribbon. The compact workstation command bar does not hide commands by tab.
 */
export type StudioRibbonTab = 'tools' | 'place' | 'view' | 'data';

/**
 * Right dock pane (Option B/C). 'inspector' edits the selected component;
 * 'verify' shows the Fit Verdict rail (containment / setback / reservations).
 * Selecting a component snaps back to 'inspector' so an edit is one click away.
 */
export type StudioRightPane = 'inspector' | 'verify';

interface StudioState {
  scenarioKey: string | null;
  tool: StudioTool;
  projection: StudioProjection;
  leftPane: StudioLeftPane;
  leftDockOpen: boolean;
  /** @deprecated Compatibility mirror for hosts using the retired ribbon. */
  ribbonTab: StudioRibbonTab;
  rightPane: StudioRightPane;
  /**
   * Primary selection — the anchor the Inspector edits and fit-selection
   * frames. It is always the last member added to `selectedIds` (or null).
   */
  selectedId: string | null;
  /**
   * Full multi-selection set (includes `selectedId`). Contextual tools —
   * align / distribute / rotate / nudge — operate on this set.
   */
  selectedIds: string[];
  contentsOpen: boolean;
  layoutPanelOpen: boolean;
  inspectorOpen: boolean;
  takeoffsOpen: boolean;
  fitOpen: boolean;
  setTool: (tool: StudioTool) => void;
  cancelActiveTool: () => void;
  setProjection: (projection: StudioProjection) => void;
  setLeftPane: (leftPane: StudioLeftPane) => void;
  toggleLeftDock: () => void;
  /** @deprecated Compatibility setter for the retired ribbon. */
  setRibbonTab: (ribbonTab: StudioRibbonTab) => void;
  setRightPane: (rightPane: StudioRightPane) => void;
  select: (id: string | null) => void;
  /** Toggle one id in/out of the multi-selection (modifier-click). */
  toggleSelect: (id: string) => void;
  /** Replace the whole selection set (e.g. Select All / marquee). */
  selectMany: (ids: ReadonlyArray<string>) => void;
  toggleContents: () => void;
  toggleLayoutPanel: () => void;
  toggleInspector: () => void;
  toggleTakeoffs: () => void;
  toggleFit: () => void;
  resetForScenario: (scenarioKey: string) => void;
}

const INITIAL = {
  tool: 'select' as StudioTool,
  // Blueprint is the landing projection — the successor of the old default
  // "Plan sheet" tab (and it renders without a Maps API provider).
  projection: 'blueprint' as StudioProjection,
  // The left dock is Layers-only now (layout controls moved to the command bar).
  leftPane: 'layers' as StudioLeftPane,
  leftDockOpen: true,
  ribbonTab: 'tools' as StudioRibbonTab,
  // Verification is useful before a component is selected. Selection
  // automatically reveals the Inspector.
  rightPane: 'verify' as StudioRightPane,
  selectedId: null as string | null,
  selectedIds: [] as string[],
  // Compatibility mirrors: the left dock shows Layers (contents) only.
  contentsOpen: true,
  layoutPanelOpen: false,
  inspectorOpen: true,
  takeoffsOpen: false,
  fitOpen: false,
};

/**
 * FULL Map/Blueprint tool parity: every tool works in both projections. The
 * blueprint converts its sheet clicks to world coords (`blueprintToLocal` →
 * `latLngForLocalAxes`) for single-click placement AND multi-point drawing, so
 * switching projection only swaps the background, never the toolset.
 */
export function toolAvailableOnBlueprint(_tool: StudioTool): boolean {
  return true;
}

export const useStudioStore = create<StudioState>((set, get) => ({
  scenarioKey: null,
  ...INITIAL,
  setTool: (tool) => set((state) => {
    if (tool === 'select') return { tool };
    // Multi-point draw tools consume WGS84 paths and are map-only; single-click
    // tools (place-*, connect) also run on the blueprint. Never arm a map-only
    // tool behind the Blueprint sheet.
    if (state.projection !== 'map' && !toolAvailableOnBlueprint(tool)) {
      return { tool: 'select' as StudioTool };
    }
    // Arming a draw/place tool reveals the Layers tree so the new object is
    // immediately visible; verification steps aside until a selection exists.
    return {
      tool,
      leftPane: 'layers',
      leftDockOpen: true,
      contentsOpen: true,
      layoutPanelOpen: false,
      inspectorOpen: true,
      rightPane: state.selectedId == null ? 'verify' : 'inspector',
    };
  }),
  cancelActiveTool: () => set({ tool: 'select' }),
  // Multi-point draw tools rely on world-map coordinates; switching to Blueprint
  // disarms only those, leaving single-click tools (place-*, connect) live so
  // the same features work in both projections.
  setProjection: (projection) => set((state) => ({
    projection,
    ...(projection === 'blueprint' && !toolAvailableOnBlueprint(state.tool)
      ? { tool: 'select' as StudioTool }
      : {}),
  })),
  setLeftPane: (leftPane) => set({
    leftPane,
    leftDockOpen: true,
    contentsOpen: leftPane === 'layers',
    layoutPanelOpen: leftPane === 'layout',
  }),
  toggleLeftDock: () => set((state) => {
    const leftDockOpen = !state.leftDockOpen;
    return {
      leftDockOpen,
      contentsOpen: leftDockOpen && state.leftPane === 'layers',
      layoutPanelOpen: leftDockOpen && state.leftPane === 'layout',
    };
  }),
  setRibbonTab: (ribbonTab) => set({ ribbonTab }),
  setRightPane: (rightPane) => set({ rightPane, inspectorOpen: true }),
  // Selecting a component prioritises editing; clearing selection restores
  // the actionable verification rail rather than an empty inspector. A plain
  // select collapses any multi-selection down to the single clicked id.
  select: (selectedId) => set(selectedId == null
    ? { selectedId, selectedIds: [], rightPane: 'verify' }
    : { selectedId, selectedIds: [selectedId], rightPane: 'inspector', inspectorOpen: true }),
  toggleSelect: (id) => set((state) => {
    const has = state.selectedIds.includes(id);
    const selectedIds = has
      ? state.selectedIds.filter(current => current !== id)
      : [...state.selectedIds, id];
    if (selectedIds.length === 0) {
      return { selectedIds, selectedId: null, rightPane: 'verify' as StudioRightPane };
    }
    // Primary stays put unless it was the id just removed; then the most
    // recently added survivor becomes the anchor.
    const selectedId = has && state.selectedId === id
      ? selectedIds[selectedIds.length - 1]!
      : has
        ? state.selectedId
        : id;
    return { selectedIds, selectedId, rightPane: 'inspector' as StudioRightPane, inspectorOpen: true };
  }),
  selectMany: (ids) => set(ids.length === 0
    ? { selectedIds: [], selectedId: null, rightPane: 'verify' as StudioRightPane }
    : { selectedIds: [...ids], selectedId: ids[ids.length - 1]!, rightPane: 'inspector' as StudioRightPane, inspectorOpen: true }),
  // Legacy pane actions now behave like mutually-exclusive dock tabs.
  toggleContents: () => set((state) => {
    const closing = state.leftDockOpen && state.leftPane === 'layers';
    return closing
      ? { leftDockOpen: false, contentsOpen: false, layoutPanelOpen: false }
      : {
          leftDockOpen: true,
          leftPane: 'layers',
          contentsOpen: true,
          layoutPanelOpen: false,
        };
  }),
  toggleLayoutPanel: () => set((state) => {
    const closing = state.leftDockOpen && state.leftPane === 'layout';
    return closing
      ? { leftDockOpen: false, contentsOpen: false, layoutPanelOpen: false }
      : {
          leftDockOpen: true,
          leftPane: 'layout',
          contentsOpen: false,
          layoutPanelOpen: true,
        };
  }),
  toggleInspector: () => set((state) => ({
    inspectorOpen: !state.inspectorOpen,
    ...(!state.inspectorOpen && state.selectedId == null
      ? { rightPane: 'verify' as StudioRightPane }
      : {}),
  })),
  // Drawers share the same overlay surface, so only one can be expanded.
  toggleTakeoffs: () => set((state) => ({
    takeoffsOpen: !state.takeoffsOpen,
    fitOpen: false,
  })),
  toggleFit: () => set((state) => ({
    fitOpen: !state.fitOpen,
    takeoffsOpen: false,
  })),
  resetForScenario: (scenarioKey) => {
    if (get().scenarioKey === scenarioKey) return;
    set({ scenarioKey, ...INITIAL });
  },
}));
